import React, { useEffect, useRef, useState } from "react";
import { Bar } from "react-chartjs-2";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

import ChartDataLabels from "chartjs-plugin-datalabels";

import {
  Card,
  Skeleton,
  Box,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";

import axios from "axios";
import { baseURL } from "../../utils/StaticVariables";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { authState, popupState } from "../../Recoil/RecoilState";
import DesBtn from "../Reusable/DesBtn";
import ChartFilter from "./Filter";
import OrderFilter from "./OrderFilter";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

const AverageMaleWeekdayChart = () => {
  const theme = useTheme();

  const [dataList, setDataList] = useState([]);
  const [loading, setLoading] = useState(false);

  const setPopup = useSetRecoilState(popupState);
  const { token } = useRecoilValue(authState);

  const childRef = useRef(null);
  const [filter, setFilter] = useState({});

  // -----------------------------
  // Fetch Data
  // -----------------------------
  const fetchData = async () => {
    try {
      setLoading(true);

      const { data } = await axios.get(
        `${baseURL}analytics/zones/gender-by-weekday`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { ...filter, gender: "male" },
        }
      );

      setDataList(data.data || []);
    } catch (error) {
      console.error("Error fetching frames:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filter]);

  // -----------------------------
  // Chart Data Transformation
  // -----------------------------
  const weekdaysOrder = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];

  const zones = [...new Set(dataList.map((i) => i.zone))];
  const labels = weekdaysOrder;

  // ألوان ديناميكية من الـ theme
  const paletteColors = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.info.main,
    theme.palette.success.main,
    theme.palette.warning.main,
    theme.palette.error.main,
  ];

  const datasets = zones.map((zone, index) => {
    const color = paletteColors[index % paletteColors.length];

    return {
      label: zone,
      data: labels.map((day) => {
        const found = dataList.find(
          (i) => i.zone === zone && i.weekday_name === day
        );
        return found ? found.avg_count : 0;
      }),
      backgroundColor: alpha(color, 0.85),
      borderRadius: 6,
    };
  });

  const data = { labels, datasets };

  // -----------------------------
  // Chart Options (Theme-based)
  // -----------------------------
  const textColor = theme.palette.text.primary;
  const gridColor = theme.palette.divider;

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
        align: "end",
        labels: {
          color: textColor,
        },
      },
      title: {
        display: true,
        text: "Average Male Count by Zone per Weekday",
        color: textColor,
      },
      tooltip: {
        backgroundColor: theme.palette.background.paper,
        titleColor: textColor,
        bodyColor: textColor,
        borderColor: gridColor,
        borderWidth: 1,
      },
      // ✅ الأرقام داخل الأعمدة
      datalabels: {
        anchor: "center",
        align: "center",
        color: (ctx) =>
          theme.palette.getContrastText(
            ctx.dataset.backgroundColor
          ),
        font: {
          weight: "bold",
          size: 12,
        },
        formatter: (val) => {
          if (val === null || val === 0) return "";
          return val.toFixed(2);
        },
      },
    },
    scales: {
      x: {
        ticks: {
          color: textColor,
        },
        grid: {
          color: gridColor,
        },
        title: { display: true, text: "Weekday", color: textColor }
      },
      y: {
        beginAtZero: true,
        ticks: {
          color: textColor,
        },
        grid: {
          color: gridColor,
        },
        title: { display: true, text: "Average Male Count", color: textColor }
      },
    },
  };

  // -----------------------------
  // Filter Popup
  // -----------------------------
  const openFilter = () => {
    setPopup({
      isOpen: true,
      title: "Select Date, Time and Location Filters",
      content: (
        <OrderFilter
          ref={childRef}
          filter={filter}
          changeFilterHandle={setFilter}
        />
      ),
      sendReq: () => childRef.current?.submit(),
    });
  };

  return (
    <Card sx={{ p: 2 }}>
      <DesBtn text="Filter" handle={openFilter}>
        <FilterAltOutlinedIcon />
      </DesBtn>

      {loading ? (
        <Skeleton
          variant="rectangular"
          height="500px"
          sx={{ borderRadius: 2 }}
        />
      ) : (
        <Box sx={{ height: "500px" }}>
          <Bar data={data} options={options} />
        </Box>
      )}
    </Card>
  );
};

export default AverageMaleWeekdayChart;
